import { useEffect, useState } from "react";

// The token read on this page (path), kept for a second read of the same page: React's development
// mode calls state initializers twice, and the first read has already cleared the address bar.
let lastRead: { path: string; token: string } | null = null;

/**
 * Reads `#token=…` from the URL once and removes it from the address bar and history, so the secret
 * never reaches servers, logs or anything that reads `location` later.
 */
export function readFragmentToken(): string | null {
  const path = window.location.pathname;
  const token = new URLSearchParams(window.location.hash.slice(1)).get("token");
  if (token) {
    window.history.replaceState(null, "", path);
    lastRead = { path, token };
    return token;
  }
  return lastRead?.path === path ? lastRead.token : null;
}

/**
 * The page's link token, following a new link pasted into the same tab: only the fragment changes
 * then, so the browser doesn't reload and the page must read it again.
 */
export function useFragmentToken(): string | null {
  const [token, setToken] = useState(readFragmentToken);
  useEffect(() => {
    const onHashChange = () => {
      const next = readFragmentToken();
      if (next) setToken(next);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);
  return token;
}

/** For tests: forget the token read earlier on this page. */
export function resetFragmentToken() {
  lastRead = null;
}
